/**
 * Design tokens (docs/design/charte.md § 1), defined once and grouped as in the charter.
 * The UI reads them as `--kg-*` CSS variables; MapLibre takes the hex strings,
 * Three.js the numbers from `threeColor`.
 */
export const tokenGroups = {
  map: {
    land: "#F3EBDD",
    "land-alt": "#E9DFCB",
    "land-edge": "#D8C8A8",
    water: "#C4D3DB",
    "water-deep": "#AFC2CD",
    "water-line": "#8FA6B2",
    "road-major": "#FFFFFF",
    "road-major-casing": "#D9CCB4",
    "road-minor": "#FBF7F0",
    "rail-base": "#9C9183",
    park: "#E3E6CF",
    wood: "#DDE2C8",
  },
  ink: {
    ink: "#2F2A24",
    "ink-soft": "#6B6257",
    paper: "#FBF8F2",
    "paper-raised": "#FFFFFF",
    rule: "#E2D8C6",
    focus: "#3D6FA8",
  },
  modes: {
    "mode-road": "#C8553D",
    "mode-rail": "#5B4E8C",
    "mode-river": "#2A8C82",
    "mode-sea": "#1F4E7A",
    "mode-air": "#D9922E",
    "mode-hub": "#5E8C4A",
  },
  states: {
    ok: "#4F7D4A",
    warn: "#E0A23A",
    danger: "#B23A48",
    blocked: "#7A2E3A",
  },
  traffic: {
    "traffic-0": "#9BBF85",
    "traffic-1": "#E7C35A",
    "traffic-2": "#E08A3C",
    "traffic-3": "#B23A48",
  },
  destinations: {
    "dest-a": "#4E79A7",
    "dest-b": "#F28E2B",
    "dest-c": "#59A14F",
    "dest-d": "#E15759",
    "dest-e": "#76B7B2",
    "dest-f": "#EDC948",
    "dest-g": "#B07AA1",
    "dest-h": "#9C755F",
  },
  night: {
    "night-sky": "#1B2433",
    "night-light": "#FFE9B0",
  },
} as const;

type Groups = typeof tokenGroups;
export type TokenGroup = keyof Groups;
export type TokenName = { [G in TokenGroup]: keyof Groups[G] }[TokenGroup];

/** Flat view of every token, `name → #RRGGBB`. */
export const tokens = Object.assign({}, ...Object.values(tokenGroups)) as Readonly<
  Record<TokenName, string>
>;

/** Translucent derivatives defined by the charter (alpha in 0–1). */
export const overlays = {
  graticule: { color: "#C9BBA3", alpha: 0.4 },
  "crisis-fill": { color: "#B23A48", alpha: 0.12 },
} as const;

export const fonts = {
  title: '"Fraunces Variable", Georgia, serif',
  ui: '"Inter Variable", system-ui, sans-serif',
  mono: '"JetBrains Mono Variable", ui-monospace, monospace',
} as const;

/** Three.js colour (`0xRRGGBB`). */
export function threeColor(name: TokenName): number {
  return Number.parseInt(tokens[name].slice(1), 16);
}

/** MapLibre colour: hex string, or `rgba()` for the translucent overlays. */
export function mapColor(name: TokenName | keyof typeof overlays): string {
  if (name in overlays) {
    const { color, alpha } = overlays[name as keyof typeof overlays];
    const n = Number.parseInt(color.slice(1), 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  return tokens[name as TokenName];
}

export function cssVariables(): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) vars[`--kg-${name}`] = value;
  for (const name of Object.keys(overlays))
    vars[`--kg-${name}`] = mapColor(name as keyof typeof overlays);
  for (const [name, value] of Object.entries(fonts)) vars[`--kg-font-${name}`] = value;
  return vars;
}

export function applyTokens(root: HTMLElement): void {
  for (const [name, value] of Object.entries(cssVariables())) root.style.setProperty(name, value);
}
